import { useState } from 'react';

export default function HomePage() {
  const [selectedVehicle, setSelectedVehicle] = useState('sedan');
  const [sliderPosition, setSliderPosition] = useState(50);

  const vehicleTypes = [
    { id: 'sedan', name: 'Sedan', icon: 'ri-car-line' },
    { id: 'suv5', name: 'SUV (5 Seats)', icon: 'ri-car-line' },
    { id: 'suv7', name: 'SUV (7 Seats)', icon: 'ri-car-line' },
    { id: 'minivan', name: 'Minivan', icon: 'ri-car-line' },
    { id: 'truck', name: 'Pickup Truck', icon: 'ri-truck-line' }
  ];

  const pricing = {
    sedan: { interior: 189.99, showroom: 239.99 },
    suv5: { interior: 214.99, showroom: 264.99 },
    suv7: { interior: 229.99, showroom: 279.99 },
    minivan: { interior: 249.99, showroom: 299.99 },
    truck: { interior: 229.99, showroom: 279.99 }
  };

  const currentPricing = pricing[selectedVehicle as keyof typeof pricing];

  const handleSliderMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, percentage)));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-lg border-b border-red-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-red-800 rounded-xl flex items-center justify-center shadow-lg">
                <i className="ri-car-washing-line text-white text-2xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                <span className="text-red-600">Absolute</span>
                <span className="text-orange-500 ml-2">Mobile Detailing</span>
              </h1>
            </div>
            <a 
              href="tel:705-806-8712" 
              className="bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl px-6 py-3 text-sm rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Call (705) 806-8712
            </a>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/80 via-red-800/70 to-orange-600/60"></div>
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-left">
                  <h1 className="text-6xl md:text-7xl font-black text-white mb-6 leading-tight">
                    <span className="text-orange-400">Premium</span><br />
                    <span className="text-red-300">Auto Care</span><br />
                    <span className="text-3xl md:text-4xl font-light text-gray-200">At Your Doorstep</span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
                    Professional mobile detailing service delivering exceptional results across the Greater Toronto Area. Your vehicle deserves top-tier treatment.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 mb-12">
                    <a 
                      href="tel:705-806-8712" 
                      className="bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-xl hover:shadow-2xl px-8 py-4 text-lg rounded-full font-bold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105"
                    >
                      <i className="ri-phone-line mr-2"></i>Call (705) 806-8712
                    </a>
                    <a 
                      href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="border-2 border-red-300 text-red-300 hover:bg-red-300 hover:text-red-900 px-8 py-4 text-lg rounded-full font-bold transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105"
                    >
                      <i className="ri-calendar-line mr-2"></i>Book Online
                    </a>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <button className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer transform hover:scale-105">
                      <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-map-pin-line text-red-400 text-2xl"></i>
                      </div>
                      <h3 className="text-white font-bold mb-2">Mobile Detailing</h3>
                      <p className="text-gray-300 text-sm">We come to you</p>
                    </button>
                    <button className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer transform hover:scale-105">
                      <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-store-line text-red-400 text-2xl"></i>
                      </div>
                      <h3 className="text-white font-bold mb-2">Professional Service</h3>
                      <p className="text-gray-300 text-sm">Expert care</p>
                    </button>
                    <button className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 cursor-pointer transform hover:scale-105">
                      <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-steering-2-line text-red-400 text-2xl"></i>
                      </div>
                      <h3 className="text-white font-bold mb-2">Premium Products</h3>
                      <p className="text-gray-300 text-sm">Quality guaranteed</p>
                    </button>
                  </div>
                </div>
                <div className="hidden lg:block">
                  <div className="relative">
                    <img 
                      alt="Professional mobile car detailing service showing luxury car interior with premium red leather seats being cleaned by expert technicians using professional equipment and premium products in a modern garage setting" 
                      className="rounded-3xl shadow-2xl object-cover w-full h-[600px] border-4 border-white/20" 
                      src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/0c6fb31750e0ae6f1a3b057aac7a4b7f.jpeg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-red-400 text-3xl"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-red-50">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-20">
                <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                  About <span className="text-red-600">Absolute</span> <span className="text-orange-500">Mobile Detailing</span>
                </h2>
                <div className="w-32 h-2 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-8 rounded-full"></div>
              </div>
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <img 
                    alt="Professional auto detailing service showing skilled technician carefully cleaning luxury vehicle exterior with premium foam and microfiber cloths in bright clean environment with modern equipment and tools" 
                    className="rounded-3xl shadow-2xl object-cover w-full h-[500px] border-8 border-white" 
                    src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/9a9c1bea6067e233cae6b8e6e058cb90.jpeg"
                  />
                </div>
                <div className="space-y-8">
                  <p className="text-xl text-gray-700 leading-relaxed">
                    At Absolute Mobile Detailing, we're passionate about delivering exceptional automotive care throughout the Greater Toronto Area. Our team of skilled professionals brings years of experience and dedication to perfection to every vehicle we service.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    We understand that your vehicle is more than just transportation—it's an investment that deserves the highest level of care. That's why we use only premium products and state-of-the-art equipment to ensure your car receives the top-tier treatment it deserves.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed">
                    Serving the Greater Toronto Area with pride, we bring professional detailing services directly to your doorstep, saving you time while delivering exceptional results that exceed expectations.
                  </p>
                  <div className="grid grid-cols-2 gap-8 mt-12">
                    <div className="text-center p-8 bg-white rounded-3xl shadow-xl border-2 border-red-100 hover:border-red-300 transition-all duration-300">
                      <div className="text-4xl font-black text-red-600 mb-3">750+</div>
                      <div className="text-gray-600 font-semibold">Cars Detailed</div>
                    </div>
                    <div className="text-center p-8 bg-white rounded-3xl shadow-xl border-2 border-orange-100 hover:border-orange-300 transition-all duration-300">
                      <div className="text-4xl font-black text-orange-500 mb-3">100%</div>
                      <div className="text-gray-600 font-semibold">Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Our <span className="text-red-600">Premium</span> <span className="text-orange-500">Services</span>
              </h2>
              <div className="w-32 h-2 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
                Delivering exceptional service with quality you can trust. Choose from our range of professional detailing services, all performed at your location across the Greater Toronto Area.
              </p>
            </div>

            {/* Vehicle Type Selector */}
            <div className="max-w-6xl mx-auto mb-16">
              <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-3xl p-3 shadow-2xl">
                <div className="flex flex-wrap justify-center gap-2">
                  {vehicleTypes.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      onClick={() => setSelectedVehicle(vehicle.id)}
                      className={`flex flex-col items-center px-6 py-4 rounded-2xl transition-all duration-300 cursor-pointer whitespace-nowrap min-w-[140px] font-bold ${
                        selectedVehicle === vehicle.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      <i className={`${vehicle.icon} text-2xl mb-2`}></i>
                      <span className="text-sm">{vehicle.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Service Packages */}
            <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Interior Package */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-red-100 hover:border-red-300 transition-all duration-300 hover:transform hover:scale-105">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <i className="ri-home-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-wide">
                    Interior<br />Only
                  </h3>
                  <div className="text-5xl font-black text-red-600 mb-6">
                    <span className="text-2xl">$</span>{currentPricing.interior}
                  </div>
                </div>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Thoroughly vacuum of floors, seats, trunk and cabin</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Thoroughly clean dust/grime in cracks and crevices using compressed-air</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Thoroughly clean of doors, console, panels, plastics, dash</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Complete interior Steam cleaning</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Windows and door jambs, rubber mats cleaned</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Dash, doors and plastics dyed</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Salt stain removal</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span className="font-medium">Carpet and seats extracted and shampooed</span>
                  </li>
                </ul>
                <button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 cursor-pointer block text-center shadow-lg hover:shadow-xl whitespace-nowrap">
                  Select Package
                </button>
              </div>

              {/* Showroom Package */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-orange-100 hover:border-orange-300 transition-all duration-300 hover:transform hover:scale-105 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  MOST POPULAR
                </div>
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <i className="ri-star-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-wide">
                    Showroom<br />Package
                  </h3>
                  <div className="text-5xl font-black text-orange-500 mb-6">
                    <span className="text-2xl">$</span>{currentPricing.showroom}
                  </div>
                </div>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-500 mr-3 text-xl"></i>
                    <span className="font-medium">Everything in Interior Package</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-500 mr-3 text-xl"></i>
                    <span className="font-medium">Foam Cannon pre-wash to safely prep the vehicles and melt away dirt or grime</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-500 mr-3 text-xl"></i>
                    <span className="font-medium">Wheel clean and tire shine dressing</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-orange-500 mr-3 text-xl"></i>
                    <span className="font-medium">Hand wash the entire car</span>
                  </li>
                </ul>
                <button className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 cursor-pointer block text-center shadow-lg hover:shadow-xl whitespace-nowrap">
                  Select Package
                </button>
              </div>

              {/* Regular Wash */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-gray-100 hover:border-gray-300 transition-all duration-300 hover:transform hover:scale-105">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <i className="ri-shield-star-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase tracking-wide">
                    Regular<br />Wash
                  </h3>
                  <div className="text-5xl font-black text-gray-600 mb-6">
                    <span className="text-2xl">$</span>149.99
                  </div>
                </div>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-gray-500 mr-3 text-xl"></i>
                    <span className="font-medium">Exterior wash and rinse</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-gray-500 mr-3 text-xl"></i>
                    <span className="font-medium">Wheel cleaning</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-gray-500 mr-3 text-xl"></i>
                    <span className="font-medium">Basic interior vacuum</span>
                  </li>
                  <li className="flex items-center text-gray-700">
                    <i className="ri-check-line text-gray-500 mr-3 text-xl"></i>
                    <span className="font-medium">Window cleaning</span>
                  </li>
                </ul>
                <button className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-300 cursor-pointer block text-center shadow-lg hover:shadow-xl whitespace-nowrap">
                  Select Package
                </button>
              </div>
            </div>

            {/* Additional Services */}
            <div className="max-w-5xl mx-auto mt-20">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-10 shadow-2xl border-2 border-red-100">
                <h3 className="text-3xl font-black text-gray-900 mb-12 text-center">Additional Premium Services</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">Pet Hair Removal</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$20-40</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Expert removal of pet hair from the interior of your vehicle, leaving it clean and fresh.</p>
                  </div>
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">Engine Shampoo</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$40</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Thorough cleaning and degreasing of your vehicle's engine bay, improving performance and appearance.</p>
                  </div>
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">Headlight Restoration</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$80</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Restoration and polishing of cloudy or faded headlights, enhancing visibility and safety.</p>
                  </div>
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">Clay Bar Treatment</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$50</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Application of a clay bar treatment to remove surface contaminants and restore a smooth finish.</p>
                  </div>
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">1 Year Ceramic Coating</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$70</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">High-quality ceramic coating application for long-lasting protection and a glossy shine.</p>
                  </div>
                  <div className="bg-white border-2 rounded-2xl p-4 transition-all duration-300 cursor-pointer hover:shadow-lg border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-bold text-gray-900">Smell Removal</h5>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-red-600">$50</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Effective odor elimination treatment, targeting and neutralizing unpleasant smells.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Reviews */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Customer <span className="text-red-600">Reviews</span>
              </h2>
              <div className="w-32 h-2 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
                See what our satisfied customers have to say about our premium detailing services
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-10 shadow-2xl border-2 border-red-100">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                      <i className="ri-google-fill text-white text-2xl"></i>
                    </div>
                  </div>
                  <h3 className="text-3xl font-black text-gray-900 mb-6">Google Reviews</h3>
                  <div className="flex items-center justify-center mb-6">
                    <div className="flex space-x-1">
                      <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                      <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                      <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                      <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                      <i className="ri-star-fill text-yellow-400 text-2xl"></i>
                    </div>
                    <span className="ml-3 text-2xl font-bold text-gray-900">5.0</span>
                  </div>
                  <p className="text-gray-700 mb-8 text-lg">
                    Read authentic reviews from our satisfied customers and see why Absolute Mobile Detailing is the trusted choice for premium mobile detailing services.
                  </p>
                  <div className="grid md:grid-cols-3 gap-6 mt-12">
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex space-x-1">
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4 italic">
                        "Exceptional service! My car looks brand new after their detailing. Highly recommend Absolute Mobile Detailing!"
                      </p>
                      <div className="text-sm text-gray-500 font-semibold">- Sarah M.</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex space-x-1">
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4 italic">
                        "Professional, reliable, and amazing results. They came to my location and did an incredible job!"
                      </p>
                      <div className="text-sm text-gray-500 font-semibold">- Mike R.</div>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex space-x-1">
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                          <i className="ri-star-fill text-yellow-400"></i>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4 italic">
                        "Best detailing service in the area! Attention to detail is outstanding. Will definitely use again."
                      </p>
                      <div className="text-sm text-gray-500 font-semibold">- Jennifer L.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Before & After Gallery */}
        <section id="gallery" className="py-24 bg-gradient-to-br from-gray-50 to-red-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-20">
              <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                Before & <span className="text-red-600">After</span> <span className="text-orange-500">Gallery</span>
              </h2>
              <div className="w-32 h-2 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-8 rounded-full"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-4">
                See the incredible transformation we achieve with our professional detailing services
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Drag the slider to reveal the amazing before and after results
              </p>
            </div>
            <div className="max-w-5xl mx-auto">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-red-100">
                <div className="p-10">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-3xl font-black text-gray-900">Professional Auto Detailing Transformation</h3>
                    <button className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-2xl transition-all duration-300 flex items-center gap-2 font-bold shadow-lg">
                      <i className="ri-upload-line"></i>Upload Photos
                    </button>
                  </div>
                  <div 
                    className="relative h-96 rounded-2xl overflow-hidden group cursor-ew-resize select-none border-4 border-gray-200"
                    onMouseMove={handleSliderMove}
                  >
                    <img 
                      alt="Car before professional detailing showing dirty exterior with dust grime and water spots on dark colored sedan in simple clean background setting" 
                      className="absolute inset-0 w-full h-full object-cover" 
                      draggable={false}
                      src="https://public.readdy.ai/ai/img_res/2241d13b60994ac7eee3dff159903b3d.jpg"
                    />
                    <div 
                      className="absolute inset-0 overflow-hidden transition-all duration-100 ease-out"
                      style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                    >
                      <img 
                        alt="Car after professional detailing showing pristine clean exterior with glossy shine and perfect finish on same dark colored sedan in matching clean background setting" 
                        className="w-full h-full object-cover" 
                        draggable={false}
                        src="https://public.readdy.ai/ai/img_res/eacc3b14a1d8cd9fcd8e720e168ba3cd.jpg"
                      />
                    </div>
                    <div 
                      className="absolute top-0 bottom-0 w-1 bg-white shadow-2xl cursor-ew-resize z-10 transition-all duration-100"
                      style={{ left: `${sliderPosition}%` }}
                    >
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white rounded-full shadow-2xl flex items-center justify-center transition-all duration-200 hover:scale-110 border-4 border-red-200">
                        <i className="ri-drag-move-line text-red-600 text-xl font-bold"></i>
                      </div>
                    </div>
                    <div className="absolute top-6 left-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-2xl font-black shadow-lg text-lg">
                      AFTER
                    </div>
                    <div className="absolute top-6 right-6 bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-2xl font-black shadow-lg text-lg">
                      BEFORE
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Section */}
        <section className="py-24 bg-gradient-to-r from-red-600 via-red-700 to-orange-600">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-5xl md:text-6xl font-black text-white mb-8">Book Your Appointment Today</h2>
              <p className="text-xl text-red-100 mb-12 max-w-3xl mx-auto">
                Ready to give your vehicle the top-tier care it deserves? Schedule your appointment online or call us directly.
              </p>
              <div className="bg-white rounded-3xl p-10 shadow-2xl mb-12 border-4 border-red-200">
                <h3 className="text-3xl font-black text-gray-900 mb-8">Schedule Online</h3>
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
                    <div className="flex items-center justify-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                        <i className="ri-calendar-check-line text-white text-2xl"></i>
                      </div>
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Book Your Mobile Detailing Appointment</h4>
                    <div className="mb-6">
                      <a 
                        href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 text-lg whitespace-nowrap"
                      >
                        <i className="ri-calendar-line mr-2"></i>Schedule Your Appointment
                      </a>
                    </div>
                    <p className="text-gray-600 text-sm">Click above to schedule your appointment</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                    <div className="text-center">
                      <i className="ri-calendar-2-line text-red-500 text-3xl mb-4 block"></i>
                      <h5 className="font-bold text-gray-900 mb-2">Book Online</h5>
                      <p className="text-gray-600 text-sm">Easy and convenient appointment scheduling</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                    <div className="text-center">
                      <i className="ri-time-line text-orange-500 text-3xl mb-4 block"></i>
                      <h5 className="font-bold text-gray-900 mb-2">Flexible Timing</h5>
                      <p className="text-gray-600 text-sm">We work around your schedule</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                    <div className="text-center">
                      <i className="ri-map-pin-line text-red-500 text-3xl mb-4 block"></i>
                      <h5 className="font-bold text-gray-900 mb-2">Mobile Service</h5>
                      <p className="text-gray-600 text-sm">We come to your location</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-gray-50 to-red-50 rounded-2xl p-8 border-2 border-red-200">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Service Area</h4>
                  <p className="text-gray-700 mb-4">
                    We proudly serve the Greater Toronto Area with our premium mobile detailing services.
                  </p>
                  <div className="flex items-center justify-center">
                    <i className="ri-map-pin-2-line text-red-500 text-xl mr-2"></i>
                    <span className="font-semibold text-gray-900">Greater Toronto Area</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="text-red-100 mb-6 text-xl font-semibold">Or</p>
                <a 
                  href="tel:705-806-8712" 
                  className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-4 text-xl rounded-2xl transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 font-bold"
                >
                  <i className="ri-phone-line mr-2"></i>Call (705) 806-8712
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-red-800 rounded-xl flex items-center justify-center shadow-lg">
                    <i className="ri-car-washing-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold">
                    <span className="text-red-400">Absolute</span>
                    <span className="text-orange-400 ml-2">Mobile Detailing</span>
                  </h3>
                </div>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Professional mobile detailing service delivering top-tier results across the Greater Toronto Area.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-white"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-red-400 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors cursor-pointer">
                    <i className="ri-twitter-fill text-white"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center hover:bg-pink-700 transition-colors cursor-pointer">
                    <i className="ri-instagram-fill text-white"></i>
                  </a>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-6 text-red-400">Services</h4>
                <ul className="space-y-3">
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Interior Detailing</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Exterior Detailing</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Full Service Packages</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Paint Correction</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Ceramic Coating</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-6 text-orange-400">Contact Info</h4>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-400">
                    <i className="ri-phone-line mr-3 text-red-400"></i>
                    <a href="tel:705-806-8712" className="hover:text-white transition-colors cursor-pointer">(705) 806-8712</a>
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-mail-line mr-3 text-red-400"></i>
                    <a href="mailto:info@absolutemobiledetailing.com" className="hover:text-white transition-colors cursor-pointer">info@absolutemobiledetailing.com</a>
                  </li>
                  <li className="flex items-start text-gray-400">
                    <i className="ri-map-pin-line mr-3 text-red-400 mt-1"></i>
                    <span>Serving the Greater Toronto Area</span>
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-time-line mr-3 text-red-400"></i>
                    <span>Mon-Sun: 7:00 AM - 9:00 PM</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-6 text-red-400">Quick Links</h4>
                <ul className="space-y-3">
                  <li><a href="#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</a></li>
                  <li><a href="#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About</a></li>
                  <li><a href="#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Services</a></li>
                  <li><a href="#gallery" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Gallery</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-12 pt-8 text-center">
              <p className="text-gray-500">
                © 2023 Absolute Mobile Detailing. All rights reserved. | 
                <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors"> Website Builder</a>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}